import { useCallback, useEffect, useRef, useState } from 'react'
import useAxiosPrivate from './useAxiosPrivate'
import socket from '../utils/socket'

// Same rules as the badges on the Chat page: only chats flagged as unread count.
// A chat marked unread by hand (no counter) counts as 1. Muted chats are skipped unless you were @mentioned.
export const countUnread = (settings) => {
  let total = 0
  let mentioned = false

  Object.values(settings || {}).forEach((s) => {
    if (!s?.markedUnread) return

    const mentions = s.mentionCount || 0
    if (s.muted && mentions === 0) return

    if (mentions > 0) mentioned = true
    total += (s.unreadCount || 0) > 0 ? s.unreadCount : 1
  })

  return { total, mentioned }
}

const useChatUnread = (enabled) => {
  const axiosPrivate = useAxiosPrivate()
  const [settings, setSettings] = useState({})
  const timerRef = useRef(null)

  const load = useCallback(async () => {
    try {
      const { data } = await axiosPrivate.get('/api/messages/settings')
      setSettings(Object.fromEntries(data.map((s) => [String(s.peerId), s])))
    } catch (err) {
      if (err?.name !== 'CanceledError' && err?.name !== 'AbortError') {
        console.error('Unable to load unread counts', err)
      }
    }
  }, [axiosPrivate])

  // Several events can arrive together: reload once, shortly after the last one
  const scheduleLoad = useCallback(() => {
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(load, 400)
  }, [load])

  useEffect(() => {
    if (!enabled) {
      setSettings({})
      return
    }

    load()

    // While the Chat page is open it publishes its own (instant) state
    const onChanged = (e) => {
      if (e.detail) setSettings(e.detail)
      else scheduleLoad()
    }

    socket.on('newMessage', scheduleLoad)
    socket.on('chatEvent', scheduleLoad)     // missed calls
    socket.on('connect', scheduleLoad)
    window.addEventListener('chat:unread-changed', onChanged)
    const poll = setInterval(load, 60000)    // safety net

    return () => {
      clearTimeout(timerRef.current)
      clearInterval(poll)
      socket.off('newMessage', scheduleLoad)
      socket.off('chatEvent', scheduleLoad)
      socket.off('connect', scheduleLoad)
      window.removeEventListener('chat:unread-changed', onChanged)
    }
  }, [enabled, load, scheduleLoad])

  return countUnread(settings)
}

export default useChatUnread